// src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  Navigate, // Add this import
} from "react-router-dom";
import Navbar from "./navBars/Navbar";
import Home from "./components/Home";
import Login from "./components/Login";
import { AuthProvider } from "./providers/AuthProvider";
import VariableProvider from "./providers/VariableProvider";
import DataTables from "./components/DataTables";
import Weighting from "./components/Weighting";
import DataCheck from "./components/DataCheck";
import Banner from "./components/Banner";
import DataAnalysis from "./components/DataAnalysis";
import DashboardView from "./components/DashboardView";
import DashboardBuilder from "./components/DashboardBuilder";
import Sidebar from "./navBars/Sidebar";
import FreqProvider from "./providers/FreqProvider";
import SurveyElements from "./components/SurveyElements";
import SurveyBuilder from "./components/SurveyBuilder";
import QnrDesign from "./components/QnrDesign";
import Data from "./components/Data";
import CrosstabPage from "./components/CrosstabPage";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfUse from "./components/TermsOfUse";
import SSOConsentNote from "./components/SSOConsentNote";
import AdminPanel from "./components/AdminPanel";
import { DragDropContext } from "@hello-pangea/dnd";
import VariableContext from "./contexts/VariableContext";
import { ToastProvider } from "./providers/ToastProvider";
import { ConfirmProvider } from "./components/ConfirmDialog";
import ErrorBoundary from "./components/ErrorBoundary";
import useVersionCheck from "./hooks/useVersionCheck";
import { ThemeProvider } from "./contexts/ThemeProvider";
import ThemeSelector from "./components/ThemeSelector";
import ProtectedRoute from "./components/ProtectedRoute";

function App() {
  useVersionCheck();
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <ConfirmProvider>
              <Router>
                <AppContent />
              </Router>
            </ConfirmProvider>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  const location = useLocation();
  const hideNavbarRoutes = ["/login", "/", "/privacy", "/terms", "/sso-consent"];
  const hasNavbar = !hideNavbarRoutes.includes(location.pathname);

  return (
    <VariableProvider>
      <FreqProvider>
        <>
          {hasNavbar && <Navbar />}
          <Routes>
            {/* Public Routes - No authentication needed */}
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfUse />} />
            <Route path="/sso-consent" element={<SSOConsentNote />} />

            {/* Protected Routes - Authentication required */}
            <Route
              path="/orgadmin"
              element={
                <ProtectedRoute>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="/datatables"
              element={
                <ProtectedRoute>
                  <WithSidebar hasNavbar={hasNavbar} component={DataTables} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/weighting"
              element={
                <ProtectedRoute>
                  <WithSidebar hasNavbar={hasNavbar} component={Weighting} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/datacheck"
              element={
                <ProtectedRoute>
                  <WithSidebar hasNavbar={hasNavbar} component={DataCheck} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/banner"
              element={
                <ProtectedRoute>
                  <WithSidebar hasNavbar={hasNavbar} component={Banner} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dataanalysis"
              element={
                <ProtectedRoute>
                  <WithSidebar hasNavbar={hasNavbar} component={DataAnalysis} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/data"
              element={
                <ProtectedRoute>
                  <WithSidebar hasNavbar={hasNavbar} component={Data} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/crosstabmodel"
              element={
                <ProtectedRoute>
                  <CrosstabPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <WithSidebar hasNavbar={hasNavbar} component={DashboardBuilder} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboardbuilder"
              element={
                <ProtectedRoute>
                  <DashboardBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboardview"
              element={
                <ProtectedRoute>
                  <DashboardView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/surveyelement"
              element={
                <ProtectedRoute>
                  <SurveyElements />
                </ProtectedRoute>
              }
            />
            <Route
              path="/qnrdesign"
              element={
                <ProtectedRoute>
                  <QnrDesign />
                </ProtectedRoute>
              }
            />
            <Route
              path="/surveybuilder"
              element={
                <ProtectedRoute>
                  <WithSidebar hasNavbar={hasNavbar} component={SurveyBuilder} />
                </ProtectedRoute>
              }
            />

            {/* 👇 CATCH-ALL ROUTE - Redirect any unknown path to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />

          </Routes>

          <ThemeSelector position="bottom-right" />
        </>
      </FreqProvider>
    </VariableProvider>
  );
}

function WithSidebar({ component: Component, hasNavbar }) {
  const { dndHandlers } = React.useContext(VariableContext);

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (
      source.droppableId === "sidebar-questions" &&
      destination.droppableId === "sidebar-questions"
    ) {
      if (dndHandlers.sidebar) dndHandlers.sidebar(result);
    }

    if (
      source.droppableId === "sidebar-questions" &&
      (destination.droppableId === "rows" ||
        destination.droppableId === "columns")
    ) {
      if (dndHandlers.data) dndHandlers.data(result);
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div
        className={`page-with-sidebar ${hasNavbar ? "" : "no-navbar"}`}
        style={{ display: "flex" }}
      >
        <Sidebar />
        <div className="main-content">
          <Component />
        </div>
      </div>
    </DragDropContext>
  );
}

export default App;